import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import app from '../../firebase';
import Flowchart from '../academics/Flowchart';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

const db = getFirestore(app);
const ACADEMIC_YEAR_OPTIONS = Array.from({ length: 18 }, (_, index) => {
  const startYear = 2018 + index;
  return `${startYear}-${startYear + 1}`;
});

export default function RegisterCurriculum() {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [curriculumYears, setCurriculumYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [newYear, setNewYear] = useState('');
  const [yearMsg, setYearMsg] = useState('');
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const [subjects, setSubjects] = useState([]);
  const [subjectMsg, setSubjectMsg] = useState('');
  const [editingSubject, setEditingSubject] = useState(null);
  const [isSavingSubject, setIsSavingSubject] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [filterYearLevel, setFilterYearLevel] = useState('');
  const [filterSemester, setFilterSemester] = useState('');
  const [filterSubjectType, setFilterSubjectType] = useState('');
  const [filterCourseId, setFilterCourseId] = useState('');
  const [isMinorSubject, setIsMinorSubject] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [activeView, setActiveView] = useState('subjects');
  const [subjectToDelete, setSubjectToDelete] = useState(null);
  const [subjectDeleteError, setSubjectDeleteError] = useState('');

  const filteredSubjects = useMemo(() => {
    return subjects.filter(subject => {
      const matchesYear = !filterYearLevel || subject.yearLevel === filterYearLevel;
      const matchesSemester = !filterSemester || subject.semester === filterSemester;
      let matchesSubjectType = true;
      if (filterSubjectType === 'major') {
        matchesSubjectType = !subject.isMinorSubject;
      } else if (filterSubjectType === 'minor') {
        matchesSubjectType = subject.isMinorSubject;
      }
      const matchesCourseId = !filterCourseId || (subject.courseId || '').toLowerCase().includes(filterCourseId.toLowerCase());
      return matchesYear && matchesSemester && matchesSubjectType && matchesCourseId;
    });
  }, [subjects, filterYearLevel, filterSemester, filterSubjectType, filterCourseId]);

  const sortedSubjects = useMemo(() => {
    const yearOrder = {
      'First Year': 1,
      'Second Year': 2,
      'Third Year': 3,
      'Fourth Year': 4,
      'Fifth Year': 5,
      'Sixth Year': 6
    };
    const semesterOrder = {
      'First Semester': 1,
      'Second Semester': 2
    };

    const defaultSorted = [...filteredSubjects].sort((a, b) => {
      const yearA = yearOrder[a.yearLevel] || 99;
      const yearB = yearOrder[b.yearLevel] || 99;
      if (yearA !== yearB) return yearA - yearB;

      const semA = semesterOrder[a.semester] || 99;
      const semB = semesterOrder[b.semester] || 99;
      if (semA !== semB) return semA - semB;

      const idA = a.courseId?.toString().localeCompare(b.courseId || '') || 0;
      if (idA !== 0) return idA;

      return (a.courseName || '').localeCompare(b.courseName || '');
    });
    if (!sortConfig.key) return defaultSorted;

    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    return defaultSorted
      .map((subject, index) => ({ subject, index }))
      .sort((a, b) => {
        const valueA = (a.subject[sortConfig.key] || '').toString().trim();
        const valueB = (b.subject[sortConfig.key] || '').toString().trim();
        if (!valueA && valueB) return 1;
        if (valueA && !valueB) return -1;
        const comparison = valueA.localeCompare(valueB, undefined, { sensitivity: 'base', numeric: true });
        return comparison === 0 ? a.index - b.index : comparison * direction;
      })
      .map(({ subject }) => subject);
  }, [filteredSubjects, sortConfig]);

  const validateSubject = (subject) => {
    const courseId = (subject.courseId || '').toString();
    const courseName = (subject.courseName || '').toString();
    const units = (subject.units || '').toString();
    const lectureHours = (subject.lectureHours || '').toString();
    const labHours = (subject.labHours || '').toString();
    const trainingHours = (subject.trainingHours || '').toString();
    const errors = [];
    if (!courseId.trim()) errors.push('enter a Course ID');
    else if (courseId.length > 8) errors.push('Course ID must be 8 characters or fewer');
    if (!courseName.trim()) errors.push('enter a Course Name');
    else if (courseName.length > 25) errors.push('Course Name must be 25 characters or fewer');
    if (!units) errors.push('enter Units');
    else if (!/^\d{1,2}$/.test(units)) errors.push('Units must contain up to 2 digits');
    if (lectureHours && !/^\d{1,2}$/.test(lectureHours)) errors.push('Lecture Hours must contain up to 2 digits');
    if (labHours && !/^\d{1,2}$/.test(labHours)) errors.push('Lab Hours must contain up to 2 digits');
    if (trainingHours && !/^\d{1,4}$/.test(trainingHours)) errors.push('Training Hours must contain up to 4 digits');
    return errors;
  };

  const findSubjectDuplicates = async (department, id, name, excludeId = '') => {
    const q = query(collection(db, 'curriculum_subjects'), where('department', '==', department));
    const snap = await getDocs(q);
    const normalizedId = id.trim().toLowerCase();
    const normalizedName = name.trim().toLowerCase();
    const matches = snap.docs
      .filter(subjectDoc => subjectDoc.id !== excludeId)
      .map(subjectDoc => subjectDoc.data());
    return {
      courseId: matches.some(subject => normalizedId && (subject.courseId || '').toString().trim().toLowerCase() === normalizedId),
      courseName: matches.some(subject => normalizedName && (subject.courseName || '').toString().trim().toLowerCase() === normalizedName)
    };
  };

  const duplicateMessage = (duplicates, department) => {
    const fields = [];
    if (duplicates.courseId) fields.push('Course ID');
    if (duplicates.courseName) fields.push('Course Name');
    if (fields.length === 0) return '';
    return fields.length === 1
      ? `${fields[0]} is already used by another subject in ${department}.`
      : `${fields.join(' and ')} are already used by another subject in ${department}.`;
  };

  const totalUnits = useMemo(() => {
    return sortedSubjects.reduce((sum, subject) => sum + (Number(subject.units) || 0), 0);
  }, [sortedSubjects]);

  const resetSubjectForm = () => {
    setSemester('First Semester');
    setYearLevel('First Year');
    setCourseId('');
    setCourseName('');
    setLecHrs('');
    setLabHrs('');
    setTrainingHrs('');
    setUnits('');
    setIsMinorSubject(false);
  };

  const showError = (message) => setErrorModalMessage(message);

  const handleEditSubject = (subject) => {
    setEditingSubject({
      ...subject,
      semester: subject.semester || 'First Semester',
      yearLevel: subject.yearLevel || 'First Year',
      courseId: (subject.courseId || '').toString(),
      courseName: (subject.courseName || '').toString(),
      lectureHours: (subject.lectureHours || '').toString(),
      labHours: (subject.labHours || '').toString(),
      trainingHours: (subject.trainingHours || '').toString(),
      units: (subject.units || '').toString(),
      isMinorSubject: Boolean(subject.isMinorSubject)
    });
  };

  // Subject form fields
  const [semester, setSemester] = useState('First Semester');
  const [courseId, setCourseId] = useState('');
  const [courseName, setCourseName] = useState('');
  const [lecHrs, setLecHrs] = useState('');
  const [labHrs, setLabHrs] = useState('');
  const [trainingHrs, setTrainingHrs] = useState('');
  const [units, setUnits] = useState('');
  const [yearLevel, setYearLevel] = useState('First Year');

  const updateSubjectField = (setter, value) => {
    setSubjectMsg('');
    setter(value);
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const snap = await getDocs(collection(db, 'departments'));
        const names = Array.from(new Set(snap.docs.map(d => d.data().name).filter(Boolean)))
          .filter(name => name !== 'General Education');
        setDepartments(names);
        if (names.length > 0) setSelectedDepartment(current => current || names[0]);
      } catch (err) {
        console.error(err);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (!selectedDepartment) {
      setCurriculumYears([]);
      setSelectedYear('');
      return;
    }
    const fetchYears = async () => {
      const q = query(collection(db, 'curriculums'), where('department', '==', selectedDepartment));
      const snap = await getDocs(q);
      const yrs = snap.docs.map(d => d.data().year).filter(Boolean);
      setCurriculumYears(yrs);
    };
    fetchYears();
  }, [selectedDepartment]);

  useEffect(() => {
    if (!selectedDepartment || !selectedYear) {
      setSubjects([]);
      return;
    }
    const fetchSubjects = async () => {
      const q = query(
        collection(db, 'curriculum_subjects'),
        where('department', '==', selectedDepartment),
        where('year', '==', selectedYear)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSubjects(docs);
    };
    fetchSubjects();
  }, [selectedDepartment, selectedYear]);

  useEffect(() => {
    if (!courseId.trim() || !selectedDepartment) return undefined;
    let isCurrent = true;
    const normalizedCourseId = courseId.trim().toLowerCase();
    const fetchMinorSubject = async () => {
      try {
        const q = query(
          collection(db, 'curriculum_subjects'),
          where('department', '==', selectedDepartment)
        );
        const snap = await getDocs(q);
        const matches = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(subj =>
            (subj.courseId || '').toString().trim().toLowerCase() === normalizedCourseId &&
            (subj.isMinorSubject || subj.department === 'General Education')
          );

        if (!isCurrent || matches.length === 0) return;
        if (matches.length > 1) {
          setSubjectMsg(`More than one minor subject in ${selectedDepartment} uses this Course ID. Auto-fill was not applied.`);
          return;
        }
        const [matched] = matches;

        setSubjectMsg(`Minor subject detected: ${matched.courseId} ${matched.courseName}. Auto-filled details.`);
        setCourseName(matched.courseName || '');
        setLecHrs(matched.lectureHours || '');
        setLabHrs(matched.labHours || '');
        setTrainingHrs(matched.trainingHours || '');
        setUnits(matched.units || '');
        setSemester(matched.semester || 'First Semester');
        setYearLevel(matched.yearLevel || 'First Year');
        setIsMinorSubject(true);
      } catch (err) {
        console.error('Minor subject lookup failed', err);
      }
    };

    fetchMinorSubject();
    return () => {
      isCurrent = false;
    };
  }, [courseId, selectedDepartment]);

  const handleAddCurriculumYear = async (e) => {
    e && e.preventDefault();
    setYearMsg('');
    setErrorModalMessage('');
    const normalized = newYear.trim().replace(/\s*-\s*/g, '-');
    if (!normalized) {
      showError('Enter a curriculum year in YYYY-YYYY format.');
      return;
    }
    const yearRegex = /^\d{4}-\d{4}$/;
    if (!yearRegex.test(normalized)) {
      showError('Enter four digits, a hyphen, then four digits (for example, 2025-2026).');
      return;
    }
    const [s, eYear] = normalized.split('-').map(v => parseInt(v, 10));
    if (eYear !== s + 1) {
      showError('The ending year must be exactly one year after the starting year.');
      return;
    }
    const exists = curriculumYears.some(y => y.replace(/\s*-\s*/g, '-').toLowerCase() === normalized.toLowerCase());
    if (exists) {
      showError(`Curriculum year ${normalized} already exists for this department.`);
      return;
    }
    try {
      await addDoc(collection(db, 'curriculums'), { department: selectedDepartment, year: normalized });
      setYearMsg('Curriculum year added');
      setNewYear('');
      // refresh
      const q = query(collection(db, 'curriculums'), where('department', '==', selectedDepartment));
      const snap = await getDocs(q);
      setCurriculumYears(snap.docs.map(d => d.data().year));
    } catch (err) {
      console.error('Error adding curriculum year:', err);
      showError('Could not add the curriculum year. Please try again.');
    }
  };

  const handleAddSubject = async (e) => {
    e && e.preventDefault();
    if (isSavingSubject) return;
    setSubjectMsg('');
    setErrorModalMessage('');
    if (!selectedDepartment || !selectedYear) {
      showError('Select a department and curriculum year before adding a subject.');
      return;
    }
    const validationErrors = validateSubject({
      courseId,
      courseName,
      units,
      lectureHours: lecHrs,
      labHours: labHrs,
      trainingHours: trainingHrs
    });
    if (validationErrors.length > 0) {
      showError(`Please correct the following: ${validationErrors.join('; ')}.`);
      return;
    }
    setIsSavingSubject(true);
    try {
      const duplicates = await findSubjectDuplicates(selectedDepartment, courseId, courseName);
      const message = duplicateMessage(duplicates, selectedDepartment);
      if (message) {
        showError(message);
        return;
      }
      if (isMinorSubject) {
        const confirmationMessage = `This subject will be saved as a minor subject in the current department.\n\nCourse ID: ${courseId}\nCourse Name: ${courseName}\nLecture Hours: ${lecHrs || 'N/A'}\nLab Hours: ${labHrs || 'N/A'}\nTraining Hours: ${trainingHrs || 'N/A'}\nUnits: ${units}\n\nPress OK to continue.`;
        if (!window.confirm(confirmationMessage)) {
          setSubjectMsg('Minor subject save canceled');
          return;
        }
      }

      const lectureDuration = lecHrs ? `${lecHrs} hrs` : '';
      const labDuration = labHrs ? `${labHrs} hrs` : '';
      const trainingDuration = trainingHrs ? `${trainingHrs} hrs` : '';
      const docBody = {
        department: selectedDepartment,
        year: selectedYear,
        semester,
        yearLevel,
        courseId,
        courseName,
        units,
        lectureHours: lecHrs || '',
        labHours: labHrs || '',
        trainingHours: trainingHrs || '',
        isMinorSubject: Boolean(isMinorSubject),
        addedBy: 'Registrar'
      };
      if (lectureDuration) docBody.lectureSessions = [{ duration: lectureDuration }];
      if (labDuration) docBody.laboratorySessions = [{ duration: labDuration }];
      if (trainingDuration) docBody.internshipSessions = [{ duration: trainingDuration }];

      await addDoc(collection(db, 'curriculum_subjects'), docBody);
      resetSubjectForm();
      setSubjectMsg(isMinorSubject ? 'Minor subject added' : 'Subject added');
      try {
        await refreshSubjects();
      } catch (refreshError) {
        console.error('Subject added, but refreshing the curriculum list failed:', refreshError);
        showError('Subject added, but the list could not be refreshed. Please reload the curriculum.');
      }
    } catch (err) {
      console.error('Error adding curriculum subject:', err);
      showError('Could not add the subject. Please try again.');
    } finally {
      setIsSavingSubject(false);
    }
  };

  const refreshSubjects = async () => {
    const q = query(
      collection(db, 'curriculum_subjects'),
      where('department', '==', selectedDepartment),
      where('year', '==', selectedYear)
    );
    const snap = await getDocs(q);
    setSubjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleUpdateSubject = async (e) => {
    e.preventDefault();
    if (!editingSubject || isSavingEdit) return;
    setErrorModalMessage('');
    const validationErrors = validateSubject(editingSubject);
    if (validationErrors.length > 0) {
      showError(`Please correct the following: ${validationErrors.join('; ')}.`);
      return;
    }
    setIsSavingEdit(true);
    try {
      const department = editingSubject.department || selectedDepartment;
      const duplicates = await findSubjectDuplicates(
        department,
        editingSubject.courseId,
        editingSubject.courseName,
        editingSubject.id
      );
      const message = duplicateMessage(duplicates, department);
      if (message) {
        showError(message);
        return;
      }
      const confirmationMessage = editingSubject.isMinorSubject
        ? `This subject will be saved as a minor subject in ${department}.\n\nCourse ID: ${editingSubject.courseId}\nCourse Name: ${editingSubject.courseName}\n\nPress OK to continue.`
        : `Save changes to this subject?\n\nCourse ID: ${editingSubject.courseId}\nCourse Name: ${editingSubject.courseName}\n\nPress OK to continue.`;
      if (!window.confirm(confirmationMessage)) {
        return;
      }
      const lectureDuration = editingSubject.lectureHours ? `${editingSubject.lectureHours} hrs` : '';
      const labDuration = editingSubject.labHours ? `${editingSubject.labHours} hrs` : '';
      const trainingDuration = editingSubject.trainingHours ? `${editingSubject.trainingHours} hrs` : '';
      const docBody = {
        department,
        year: editingSubject.year || selectedYear,
        semester: editingSubject.semester,
        yearLevel: editingSubject.yearLevel,
        courseId: editingSubject.courseId,
        courseName: editingSubject.courseName,
        units: editingSubject.units,
        lectureHours: editingSubject.lectureHours || '',
        labHours: editingSubject.labHours || '',
        trainingHours: editingSubject.trainingHours || '',
        isMinorSubject: Boolean(editingSubject.isMinorSubject),
        addedBy: editingSubject.addedBy || 'Registrar'
      };
      if (lectureDuration) docBody.lectureSessions = [{ duration: lectureDuration }];
      if (labDuration) docBody.laboratorySessions = [{ duration: labDuration }];
      if (trainingDuration) docBody.internshipSessions = [{ duration: trainingDuration }];

      await updateDoc(doc(db, 'curriculum_subjects', editingSubject.id), docBody);
      setEditingSubject(null);
      setSubjectMsg('Subject updated');
      try {
        await refreshSubjects();
      } catch (refreshError) {
        console.error('Subject updated, but refreshing the curriculum list failed:', refreshError);
        showError('Subject updated, but the list could not be refreshed. Please reload the curriculum.');
      }
    } catch (err) {
      console.error('Error updating curriculum subject:', err);
      showError('Could not update the subject. Please try again.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = (subject) => {
    setSubjectDeleteError('');
    setSubjectToDelete(subject);
  };

  const confirmDelete = async () => {
    if (!subjectToDelete) return;
    try {
      await deleteDoc(doc(db, 'curriculum_subjects', subjectToDelete.id));
      setSubjectMsg('Deleted');
      setSubjects(prev => prev.filter(s => s.id !== subjectToDelete.id));
      setSubjectToDelete(null);
    } catch (err) {
      console.error('Error deleting curriculum subject:', err);
      setSubjectDeleteError('Could not delete the subject. ' + (err.message || 'Please try again.'));
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '32px auto', padding: 20 }}>
      <h2>Register: Curriculum Input</h2>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', marginBottom: 24, padding: 24, border: '1px solid #e1e8ed', borderRadius: 14, background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
        <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 700, color: '#22303c' }}>Department</label>
          <select value={selectedDepartment} onChange={e => { setSelectedDepartment(e.target.value); setYearMsg(''); setSubjectMsg(''); }} style={{ padding: 10, width: '100%', borderRadius: 10, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }}>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div style={{ minWidth: 260, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 700, color: '#22303c' }}>Curriculum Year</label>
          <select value={selectedYear} onChange={e => { setSelectedYear(e.target.value); setSubjectMsg(''); }} style={{ padding: 10, width: '100%', borderRadius: 10, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }}>
            <option value="">Select Year</option>
            {curriculumYears.map((y, i) => <option key={i} value={y}>{y}</option>)}
          </select>
        </div>

        <form onSubmit={handleAddCurriculumYear} style={{ display: 'flex', gap: 14, alignItems: 'flex-end', minWidth: 420, width: '100%' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontWeight: 700, color: '#22303c' }}>New Year</label>
            <select value={newYear} onChange={e => { setNewYear(e.target.value); setYearMsg(''); }} style={{ padding: 10, width: '100%', borderRadius: 10, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }}>
              <option value="">Select Year</option>
              {ACADEMIC_YEAR_OPTIONS.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
          <button type="submit" style={{ padding: '0 20px', background: '#155c2b', color: '#fff', borderRadius: 10, border: 'none', cursor: 'pointer', height: 44 }}>Add Year</button>
        </form>
      </div>
      {yearMsg && <div role="status" style={{ color: 'green', marginBottom: 12 }}>{yearMsg}</div>}

      <div role="tablist" aria-label="Curriculum view" style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'subjects', label: 'Subjects' },
          { key: 'flowchart', label: 'Flowchart' }
        ].map(view => (
          <button
            key={view.key}
            type="button"
            role="tab"
            aria-selected={activeView === view.key}
            onClick={() => setActiveView(view.key)}
            style={{
              padding: '10px 18px',
              border: '1px solid #155c2b',
              borderRadius: 8,
              background: activeView === view.key ? '#155c2b' : '#fff',
              color: activeView === view.key ? '#fff' : '#155c2b',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            {view.label}
          </button>
        ))}
      </div>

      {!selectedYear && (
        <div role="status" style={{ padding: 16, border: '1px solid #f0c36d', borderRadius: 8, color: '#664d03', background: '#fff8e1', fontWeight: 600 }}>
          Select a curriculum year to manage its subjects.
        </div>
      )}

      {selectedYear && activeView === 'subjects' && (
        <div>
          <h3>{selectedDepartment} — {selectedYear}</h3>

          <form onSubmit={handleAddSubject} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16, rowGap: 18, alignItems: 'start', marginBottom: 18, padding: 20, border: '1px solid #e1e8ed', borderRadius: 14, background: '#fff' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Year Level</label>
              <select value={yearLevel} onChange={e => updateSubjectField(setYearLevel, e.target.value)} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }}>
                <option>First Year</option>
                <option>Second Year</option>
                <option>Third Year</option>
                <option>Fourth Year</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Semester</label>
              <select value={semester} onChange={e => updateSubjectField(setSemester, e.target.value)} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }}>
                <option>First Semester</option>
                <option>Second Semester</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Course ID</label>
              <input value={courseId} maxLength={8} onChange={e => updateSubjectField(setCourseId, e.target.value.slice(0, 8))} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Course Name</label>
              <input value={courseName} maxLength={25} onChange={e => updateSubjectField(setCourseName, e.target.value.slice(0, 25))} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Lecture Hours</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={lecHrs} maxLength={2} onChange={e => updateSubjectField(setLecHrs, e.target.value.replace(/\D/g, '').slice(0, 2))} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Lab Hours (Optional)</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={labHrs} maxLength={2} onChange={e => updateSubjectField(setLabHrs, e.target.value.replace(/\D/g, '').slice(0, 2))} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Training Hours (Optional)</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={trainingHrs} maxLength={4} onChange={e => updateSubjectField(setTrainingHrs, e.target.value.replace(/\D/g, '').slice(0, 4))} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Units</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={units} maxLength={2} onChange={e => updateSubjectField(setUnits, e.target.value.replace(/\D/g, '').slice(0, 2))} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }} />
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="submit" disabled={isSavingSubject} style={{ padding: '10px 18px', background: '#155c2b', color: '#fff', borderRadius: 8, border: 'none', cursor: isSavingSubject ? 'wait' : 'pointer', opacity: isSavingSubject ? 0.7 : 1 }}>
                {isSavingSubject ? 'Checking and adding…' : 'Add Subject'}
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, color: '#333', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={isMinorSubject}
                  onChange={e => updateSubjectField(setIsMinorSubject, e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                Mark as minor subject
              </label>
              <div role="status" style={{ flex: 1, alignSelf: 'center', color: /updated|added|deleted|detected/i.test(subjectMsg) ? 'green' : '#a94442', minHeight: 24 }}>{subjectMsg}</div>
            </div>
          </form>

          <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Filter Year Level</label>
              <select value={filterYearLevel} onChange={e => setFilterYearLevel(e.target.value)} style={{ padding: 10, borderRadius: 8, border: '1px solid #ccd6dd', height: 44, width: '100%' }}>
                <option value="">All Year Levels</option>
                <option>First Year</option>
                <option>Second Year</option>
                <option>Third Year</option>
                <option>Fourth Year</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Filter Semester</label>
              <select value={filterSemester} onChange={e => setFilterSemester(e.target.value)} style={{ padding: 10, borderRadius: 8, border: '1px solid #ccd6dd', height: 44, width: '100%' }}>
                <option value="">All Semesters</option>
                <option>First Semester</option>
                <option>Second Semester</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Filter Subject Type</label>
              <select value={filterSubjectType} onChange={e => setFilterSubjectType(e.target.value)} style={{ padding: 10, borderRadius: 8, border: '1px solid #ccd6dd', height: 44, width: '100%' }}>
                <option value="">All Subjects</option>
                <option value="major">Major Subjects</option>
                <option value="minor">Minor Subjects</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Filter Course ID</label>
              <input value={filterCourseId} onChange={e => setFilterCourseId(e.target.value)} placeholder="e.g. BIO 101" style={{ padding: '0 10px', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setFilterYearLevel(''); setFilterSemester(''); setFilterSubjectType(''); setFilterCourseId(''); }} style={{ padding: '0 18px', background: '#6c757d', color: '#fff', borderRadius: 8, border: 'none', cursor: 'pointer', height: 44, minWidth: 140 }}>
                Clear Filters
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
              <thead>
                <tr style={{ background: '#f3f3f3' }}>
                  <th style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'left' }}>Semester</th>
                  <th style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'left' }}>Year Level</th>
                  <th aria-sort={sortConfig.key === 'courseId' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'} style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'left' }}>
                    <button type="button" aria-label={`Sort by Course ID${sortConfig.key === 'courseId' ? `, ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'}` : ''}`} onClick={() => setSortConfig(current => ({ key: 'courseId', direction: current.key === 'courseId' && current.direction === 'asc' ? 'desc' : 'asc' }))} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 9px', border: sortConfig.key === 'courseId' ? '1px solid #155c2b' : '1px solid transparent', borderRadius: 6, background: sortConfig.key === 'courseId' ? '#e7f3eb' : 'transparent', color: sortConfig.key === 'courseId' ? '#10451f' : 'inherit', font: 'inherit', fontWeight: 700, cursor: 'pointer' }}>
                      Course ID
                      <span aria-hidden="true" style={{ minWidth: 92, padding: '3px 7px', borderRadius: 4, background: sortConfig.key === 'courseId' ? '#155c2b' : '#e9ecef', color: sortConfig.key === 'courseId' ? '#fff' : '#4b5563', fontSize: 11, fontWeight: 800, textAlign: 'center', letterSpacing: 0.3 }}>
                        {sortConfig.key === 'courseId' ? (sortConfig.direction === 'asc' ? '▲ ASCENDING' : '▼ DESCENDING') : '↕ SORT'}
                      </span>
                    </button>
                  </th>
                  <th aria-sort={sortConfig.key === 'courseName' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'} style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'left' }}>
                    <button type="button" aria-label={`Sort by Course Name${sortConfig.key === 'courseName' ? `, ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'}` : ''}`} onClick={() => setSortConfig(current => ({ key: 'courseName', direction: current.key === 'courseName' && current.direction === 'asc' ? 'desc' : 'asc' }))} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 9px', border: sortConfig.key === 'courseName' ? '1px solid #155c2b' : '1px solid transparent', borderRadius: 6, background: sortConfig.key === 'courseName' ? '#e7f3eb' : 'transparent', color: sortConfig.key === 'courseName' ? '#10451f' : 'inherit', font: 'inherit', fontWeight: 700, cursor: 'pointer' }}>
                      Course Name
                      <span aria-hidden="true" style={{ minWidth: 92, padding: '3px 7px', borderRadius: 4, background: sortConfig.key === 'courseName' ? '#155c2b' : '#e9ecef', color: sortConfig.key === 'courseName' ? '#fff' : '#4b5563', fontSize: 11, fontWeight: 800, textAlign: 'center', letterSpacing: 0.3 }}>
                        {sortConfig.key === 'courseName' ? (sortConfig.direction === 'asc' ? '▲ ASCENDING' : '▼ DESCENDING') : '↕ SORT'}
                      </span>
                    </button>
                  </th>
                  <th style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'center' }}>Lec Hrs</th>
                  <th style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'center' }}>Lab Hrs</th>
                  <th style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'center' }}>Training Hrs</th>
                  <th style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'center' }}>Units</th>
                  <th style={{ padding: 12, border: '1px solid #dde2e6', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedSubjects.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#667f92', background: '#fff', border: '1px solid #eef2f6' }}>
                      No curriculum entries match the selected filter. Use the form above to add or adjust subjects.
                    </td>
                  </tr>
                ) : sortedSubjects.map(s => (
                  <tr key={s.id} style={{ background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{s.semester}</td>
                    <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{s.yearLevel || ''}</td>
                    <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{s.courseId}</td>
                    <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{s.courseName}</td>
                    <td style={{ padding: 12, border: '1px solid #eef2f6', textAlign: 'center' }}>{s.lectureHours || ''}</td>
                    <td style={{ padding: 12, border: '1px solid #eef2f6', textAlign: 'center' }}>{s.labHours || ''}</td>
                    <td style={{ padding: 12, border: '1px solid #eef2f6', textAlign: 'center' }}>{s.trainingHours || ''}</td>
                    <td style={{ padding: 12, border: '1px solid #eef2f6', textAlign: 'center' }}>{s.units}</td>
                    <td style={{ padding: 12, border: '1px solid #eef2f6', textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                        <button onClick={() => handleEditSubject(s)} style={{ background: '#0f6caf', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer' }}>Edit</button>
                        <button onClick={() => handleDelete(s)} style={{ background: '#dc3545', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer' }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 0 0', marginTop: 8, borderTop: '1px solid #e8edf1' }}>
            <div style={{ fontWeight: 700, color: '#22303c' }}>Total Units: {totalUnits}</div>
          </div>

        </div>
      )}

      {activeView === 'flowchart' && selectedYear && (
        <div style={{ minHeight: 400, padding: 20, border: '1px solid #e1e8ed', borderRadius: 10, background: '#fff', overflowX: 'auto' }}>
          <h3 style={{ marginTop: 0 }}>Curriculum Flowchart — {selectedDepartment}, {selectedYear}</h3>
          <Flowchart subjects={subjects} department={selectedDepartment} yearLabel={selectedYear} />
        </div>
      )}

      {editingSubject && (
        <div
          role="presentation"
          onClick={() => {
            if (!isSavingEdit) {
              setEditingSubject(null);
            }
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="edit-subject-title" onClick={e => e.stopPropagation()} style={{ width: 'min(760px, 100%)', maxHeight: '90vh', overflowY: 'auto', padding: 24, borderRadius: 12, background: '#fff', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <h3 id="edit-subject-title" style={{ marginTop: 0 }}>Edit Subject</h3>
            <form onSubmit={handleUpdateSubject} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
              {[
                { label: 'Year Level', field: 'yearLevel', type: 'select', options: ['First Year', 'Second Year', 'Third Year', 'Fourth Year'] },
                { label: 'Semester', field: 'semester', type: 'select', options: ['First Semester', 'Second Semester'] },
                { label: 'Course ID', field: 'courseId', maxLength: 8 },
                { label: 'Course Name', field: 'courseName', maxLength: 25 },
                { label: 'Lecture Hours', field: 'lectureHours', numeric: true, maxLength: 2 },
                { label: 'Lab Hours (Optional)', field: 'labHours', numeric: true, maxLength: 2 },
                { label: 'Training Hours (Optional)', field: 'trainingHours', numeric: true, maxLength: 4 },
                { label: 'Units', field: 'units', numeric: true, maxLength: 2 }
              ].map(field => (
                <label key={field.field} style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 700, color: '#333' }}>
                  {field.label}
                  {field.type === 'select' ? (
                    <select value={editingSubject[field.field]} onChange={e => setEditingSubject(current => ({ ...current, [field.field]: e.target.value }))} style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }}>
                      {field.options.map(option => <option key={option}>{option}</option>)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      inputMode={field.numeric ? 'numeric' : undefined}
                      pattern={field.numeric ? '[0-9]*' : undefined}
                      value={editingSubject[field.field]}
                      maxLength={field.maxLength}
                      onChange={e => {
                        const value = field.numeric
                          ? e.target.value.replace(/\D/g, '').slice(0, field.maxLength)
                          : e.target.value.slice(0, field.maxLength);
                        setEditingSubject(current => ({ ...current, [field.field]: value }));
                      }}
                      style={{ padding: 10, width: '100%', borderRadius: 8, border: '1px solid #ccd6dd', height: 44, boxSizing: 'border-box' }}
                    />
                  )}
                </label>
              ))}
              <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#333' }}>
                <input type="checkbox" checked={editingSubject.isMinorSubject} onChange={e => setEditingSubject(current => ({ ...current, isMinorSubject: e.target.checked }))} />
                Mark as minor subject
              </label>
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button type="button" disabled={isSavingEdit} onClick={() => setEditingSubject(null)} style={{ padding: '10px 18px', background: '#6c757d', color: '#fff', borderRadius: 8, border: 'none', cursor: isSavingEdit ? 'wait' : 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={isSavingEdit} style={{ padding: '10px 18px', background: '#155c2b', color: '#fff', borderRadius: 8, border: 'none', cursor: isSavingEdit ? 'wait' : 'pointer', opacity: isSavingEdit ? 0.7 : 1 }}>
                  {isSavingEdit ? 'Checking and saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <DeleteConfirmationModal
        open={Boolean(subjectToDelete)}
        title="Delete Curriculum Entry?"
        subtitle="Confirm curriculum deletion"
        name={subjectToDelete?.courseName || "this curriculum entry"}
        message={`Are you sure you want to delete ${subjectToDelete?.courseName || "this curriculum entry"}? This action cannot be undone.`}
        detail="Please verify the curriculum details before proceeding."
        error={subjectDeleteError}
        confirmLabel="Delete Entry"
        onCancel={() => { setSubjectToDelete(null); setSubjectDeleteError(''); }}
        onConfirm={confirmDelete}
      />
      {errorModalMessage && (
        <div
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setErrorModalMessage('');
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(15, 23, 42, 0.55)' }}
        >
          <section role="alertdialog" aria-modal="true" aria-labelledby="curriculum-error-title" aria-describedby="curriculum-error-message" style={{ width: '100%', maxWidth: 460, padding: 26, borderRadius: 12, background: '#fff', boxShadow: '0 18px 48px rgba(0,0,0,0.24)' }}>
            <h2 id="curriculum-error-title" style={{ margin: '0 0 12px', color: '#8a1c1c', fontSize: 21 }}>Unable to continue</h2>
            <p id="curriculum-error-message" style={{ margin: '0 0 20px', color: '#334155', lineHeight: 1.55 }}>{errorModalMessage}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" autoFocus onClick={() => setErrorModalMessage('')} style={{ padding: '9px 18px', border: 'none', borderRadius: 8, background: '#155c2b', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                OK
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
